/**
 * SpiceKindColorsTab: Admin > Units > SPICE colours, the two colours SPICE
 * sessions wear everywhere Olive shows them (the Team page's week grid,
 * its legend, the session editor's kind toggle and My week).
 *
 * One ColorSwatchPicker per kind, opening on the colour configured in
 * tenants.config.spice.kind_colors, or the product default for that kind
 * (readSpiceKindColors), each with a live preview pill drawn as the grid
 * draws an active pill, in the text colour that reads on it.
 *
 * A fill that no text colour reads on at 4.5:1 (white or the ink) is
 * refused: the picker shows the error and Save is held back; the server
 * refuses it too. A fill under 3:1 against the page, or close to the other
 * kind's fill, only warns and never blocks Save (the Map colours rule).
 * Save PUTs both colours and refreshes the tenant config; others see the
 * change within ten minutes, or on their next page load.
 */

import { useState, type ReactNode } from 'react';
import { ColorSwatchPicker } from '@/components/ColorSwatchPicker';
import { AsyncBoundary } from '@/components/primitives/AsyncBoundary';
import { Button } from '@/components/primitives/Button';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import { useSupportContact } from '@/hooks/useSupportContact';
import { contactSentence } from '@/lib/errorCopy';
import { MIN_MARKER_DELTA_E, contrastRatio, deltaE76 } from '@/lib/mapColors';
import { SPICE_PILL_TEXT_DARK, SPICE_PILL_TEXT_LIGHT, readSpiceKindColors, readableTextOn } from '@/lib/spiceKindColor';
import { useSetSpiceKindColors } from '@/lib/spiceKindColorsApi';
import type { SpiceSessionKind } from '@/lib/spiceVisitPlansApi';

export interface SpiceKindColorsTabProps {
  tenantSlug: string;
}

const KINDS: ReadonlyArray<{ kind: SpiceSessionKind; label: 'SPICE' | 'Friendly Visitor' }> = [
  { kind: 'spice', label: 'SPICE' },
  { kind: 'spice_friendly_visitor', label: 'Friendly Visitor' },
];

const UNREADABLE =
  'Text would be hard to read on this colour, so it cannot be saved. Pick a darker or a lighter shade.';

/** The least contrast a fill keeps against the page before it warns. */
const MIN_PAGE_CONTRAST = 3;

export function SpiceKindColorsTab({ tenantSlug }: SpiceKindColorsTabProps): ReactNode {
  const config = useTenantConfig(tenantSlug);
  const setColors = useSetSpiceKindColors(tenantSlug);
  const contact = useSupportContact();
  const stored = readSpiceKindColors(config.data?.config);
  const [edits, setEdits] = useState<Partial<Record<SpiceSessionKind, string>>>({});
  const [saveError, setSaveError] = useState<string | null>(null);

  const fillOf = (kind: SpiceSessionKind): string => edits[kind] ?? stored[kind].fill;
  const otherOf = (kind: SpiceSessionKind) => KINDS.find((k) => k.kind !== kind)!;

  const warningFor = (kind: SpiceSessionKind): string | null => {
    const fill = fillOf(kind);
    const other = otherOf(kind);
    const faint = contrastRatio(fill, SPICE_PILL_TEXT_LIGHT) < MIN_PAGE_CONTRAST ? 'Hard to make out against the page.' : null;
    const close =
      deltaE76(fill, fillOf(other.kind)) < MIN_MARKER_DELTA_E ? `Close to ${other.label}, may be hard to tell apart.` : null;
    return [faint, close].filter(Boolean).join(' ') || null;
  };
  const unreadable = (kind: SpiceSessionKind): boolean => readableTextOn(fillOf(kind)) === null;

  const dirty = KINDS.some(({ kind }) => edits[kind] !== undefined && edits[kind] !== stored[kind].fill);
  const anyUnreadable = KINDS.some(({ kind }) => unreadable(kind));

  async function save(): Promise<void> {
    setSaveError(null);
    try {
      await setColors.mutateAsync({ spice: fillOf('spice'), spice_friendly_visitor: fillOf('spice_friendly_visitor') });
      setEdits({});
    } catch {
      setSaveError(
        contactSentence(
          "The colors didn't save. Check you're online and try again, and if it fails again, tell {contact_system}.",
          contact,
        ),
      );
    }
  }

  return (
    <div data-testid="spice-colours-tab">
      <p className="mb-4 text-sm text-ink-soft">
        The two colours SPICE sessions wear everywhere Olive shows them: the Team page's week grid, its legend, the
        session editor and My week. Text on each colour is white or dark, whichever reads; a colour neither reads on
        cannot be saved. Others see a change within ten minutes, or on their next page load.
      </p>

      <AsyncBoundary
        what="the SPICE colours"
        isLoading={config.isLoading}
        isError={config.isError}
        error={config.error}
        onRetry={() => void config.refetch()}
      >
        <div className="flex flex-col gap-5">
          {KINDS.map(({ kind, label }) => {
            const fill = fillOf(kind);
            const text = readableTextOn(fill);
            return (
              <div key={kind} className="rounded-card border border-sage-800/[8%] bg-paper p-4">
                <ColorSwatchPicker
                  label={label}
                  value={fill}
                  onChange={(hex) => setEdits((prev) => ({ ...prev, [kind]: hex }))}
                  warning={warningFor(kind)}
                  error={text === null ? UNREADABLE : null}
                  disabled={setColors.isPending}
                />
                <div className="mt-3 max-w-[12rem]">
                  <div
                    data-testid={`spice-colours-preview-${kind}`}
                    className="relative group flex w-full min-w-0 items-center gap-1.5 overflow-hidden whitespace-nowrap rounded-pill border px-2 py-0.5 text-xs leading-5"
                    style={{ backgroundColor: fill, borderColor: fill, color: text ?? SPICE_PILL_TEXT_DARK }}
                  >
                    <span className="shrink-0 font-semibold">10:00 AM</span>
                    <span className="min-w-0 flex-1 truncate">Client name</span>
                  </div>
                </div>
              </div>
            );
          })}

          <div className="flex items-center gap-3">
            <Button
              variant="primary"
              size="sm"
              data-testid="spice-colours-save"
              disabled={!dirty || setColors.isPending || anyUnreadable}
              loading={setColors.isPending}
              onClick={() => void save()}
            >
              Save
            </Button>
            {saveError && (
              <p role="alert" className="text-sm text-danger" data-testid="spice-colours-save-error">
                {saveError}
              </p>
            )}
          </div>
        </div>
      </AsyncBoundary>
    </div>
  );
}
